import { useIntersection } from '@mantine/hooks';
import { ErrorBoundary } from '@sentry/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchCommunityFeed from 'src/Hooks/Queries/useFetchCommunityFeed';

import { CommunityCreateFeedItem } from './CommunityCreateFeedItem';
import styles from './CommunityFeed.module.scss';
import { CommunityFeedItem } from './CommunityFeedItem';
import { Memories } from './Memories';

interface Props {
  // Desktop mounts the albums as a card in the right column, so the horizontal
  // strip on top of the compose is skipped. Mobile keeps it (default true).
  showMemories?: boolean;
}

const CommunityFeed = ({ showMemories = true }: Props) => {
  const { t } = useTranslation();
  const {
    data: feedItemsData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isError,
    isLoading,
  } = useFetchCommunityFeed();

  // Fetch more by scrolling down using Intersection API
  const { ref: loadMoreRef, entry } = useIntersection({ threshold: 0.5 });
  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [entry, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const feedItems = feedItemsData?.pages.flatMap(page =>
    page.items.map(feedItem => (
      <ErrorBoundary key={feedItem.id}>
        <CommunityFeedItem feedItem={feedItem} areCommentsOpenByDefault />
      </ErrorBoundary>
    ))
  );

  return (
    <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
      {showMemories ? (
        <ErrorBoundary>
          <Memories />
        </ErrorBoundary>
      ) : null}

      <ErrorBoundary>
        <CommunityCreateFeedItem />
      </ErrorBoundary>

      {feedItems}

      {feedItems && feedItems.length !== 0 ? (
        <div className={styles.statusContainer} ref={loadMoreRef}>
          <p className={styles.statusText}>
            {isFetchingNextPage
              ? t('common.loading', 'Chargement en cours...')
              : t('common.nothingMoreToShow', 'Il n’y a plus rien !')}
          </p>
        </div>
      ) : null}

      {feedItems && feedItems.length === 0 ? (
        <div className={styles.statusContainer}>
          <p className={styles.statusText}>
            {t(
              'community.emptyCommunity',
              'Ta communauté est vide pour le moment. Reviens plus tard !'
            )}
          </p>
        </div>
      ) : null}

      {isError ? (
        <div className={styles.statusContainer}>
          <p className={styles.statusText}>
            {t('community.feedError', 'Impossible de récupérer le feed. Reviens plus tard !')}
          </p>
        </div>
      ) : null}

      {isLoading ? (
        <div className={styles.statusContainer}>
          <p className={styles.statusText}>{t('common.loading')}</p>
        </div>
      ) : null}
    </div>
  );
};

export default CommunityFeed;
